/**
 * Gunfire, resolved from the equipped weapon.
 *
 * ─── Why two tables and not one ─────────────────────────────────────────────
 * `ammoType` alone cannot place these recordings. The catalog has five
 * firearms across three ammo classes that have weapons:
 *
 *   rifle_bolt    Bolt-Action Rifle   PRIMARY    ammo=rifle
 *   carbine       Carbine Rifle       PRIMARY    ammo=rifle
 *   shotgun       Combat Shotgun      PRIMARY    ammo=rifle   <- NOT 'shotgun'
 *   pistol        Pistol              SECONDARY  ammo=pistol
 *   snub_revolver Snub Revolver       SECONDARY  ammo=pistol
 *
 * That third row said `ammo=shotgun` until 2026-09-19 and it was never true —
 * shotguns are shoulder-fired and share the `rifle` calibre (content/
 * weapons.ts). The same false premise was written down in `countCarriedAmmo`
 * and cost a real bug there (the Combat Shotgun fell back to bare hands on its
 * first trigger pull, because its calibre IS fed and the player simply carried
 * none). It is harmless here — this file keys shotguns off the ITEM KEY, for
 * exactly the reason the next paragraph but two gives — but a wrong table is
 * what the next person reads.
 *
 * `ammoType` cannot separate them: `rifle_bolt` and `carbine` are both
 * `ammo=rifle`, and only one of them is a sniper. So weapon class keys off the
 * item KEY, and `ammoType` is left to cover what does not need that precision
 * — both pistols with one entry, and any future weapon of a mapped class for
 * free. Key wins over ammo type where both match.
 *
 * ─── The keys here are NOT all equippable items ─────────────────────────────
 * Only five firearms have backend rows (the table above). The weapon KEY space
 * is much larger: `WEAPON_MODEL_BY_KEY` in `character-animations.ts` registers
 * ~40, including `m249`, `pkm`, `frf2`, `svd` and `m82`, which are
 * frontend-only models with no item rows yet — seed.ts says so in as many
 * words next to `ammo_heavy`.
 *
 * Mapping them anyway is deliberate. They are the keys those weapons will
 * carry when they become items, so the sound is already correct on the day
 * the row lands instead of being a thing someone has to remember. The asset
 * test checks these keys against `WEAPON_MODEL_BY_KEY` rather than against
 * the seed, because that is the registry they actually belong to.
 *
 * ─── The shotguns are no longer a gap ───────────────────────────────────────
 * They used to be: `shotgun`, `auto_shotgun` and `sawed_off` are all
 * equippable and all `ammoType: 'rifle'`, so all three fired the rifle report
 * as a documented stand-in. A real recording landed, and they now key to
 * `gunshotShotgun` through `SOUNDED_WEAPON`.
 *
 * Through the KEY table and not the ammo table, which is forced: their ammo
 * type is the same `rifle` every assault rifle uses, so an ammo rule cannot
 * tell a shotgun from an AKM. That makes the roster the place a FOURTH
 * shotgun goes missing — add one and it silently inherits the rifle crack
 * again. `audio-assets.test.ts` closes that loop by asserting every
 * `WEAPON_ROSTER` entry of class `shotgun`/`shotgun_short` appears here.
 *
 * Everything unmapped logs once in a dev build rather than failing silently,
 * for the reason `fire-ambience.ts` gives: this whole path swallows its errors
 * so a lost clip cannot break the world, which makes "no sound yet" and "audio
 * is broken" indistinguishable without the log.
 *
 * ─── Why there is a retrigger floor ─────────────────────────────────────────
 * The shot fires from the same pointer handler as the muzzle flash, and that
 * handler runs on every click with no rate limit — `attemptAttack`'s gameplay
 * half is a documented no-op, so nothing upstream governs cadence. Mashing
 * the button would otherwise stack twenty overlapping copies of a -3 dBFS
 * transient, which sums into distortion rather than into fast shooting.
 */
import type { MixerHandle } from './mixer';
import { TRACKS } from './tracks';

/**
 * Per-weapon overrides, by item key. Checked before `SOUNDED_AMMO`.
 *
 * Only for weapons that need to differ from their ammo class. `rifle_bolt`
 * and `carbine` are both `ammo=rifle`, and only the bolt-action gets the
 * sniper recording — which is an interpretation of Janeks's "sniper rifle
 * shot" label, since nothing in the catalog calls anything a sniper. The
 * bolt-action is the closest thing the game has.
 */
export const SOUNDED_WEAPON: Record<string, keyof typeof TRACKS> = {
  // Snipers — Janeks named `frf2` and the bolt-action; `svd` and `vss` are
  // marksman rifles by the same standard, and `m82` is the anti-materiel one.
  frf2: 'gunshotSniper',
  rifle_bolt: 'gunshotSniper',
  svd: 'gunshotSniper',
  vss: 'gunshotSniper',
  m82: 'gunshotSniper',
  // Machine guns — Janeks: *"Machine guns are m249 and PKM"*.
  m249: 'gunshotMachinegun',
  pkm: 'gunshotMachinegun',
  // Shotguns. Here rather than in SOUNDED_AMMO because they are `rifle` ammo
  // — see the header. `sawed_off` is the SECONDARY-slot one; it gets the same
  // report, since the barrel length changes the pattern, not the bang.
  shotgun: 'gunshotShotgun',
  auto_shotgun: 'gunshotShotgun',
  sawed_off: 'gunshotShotgun',
};

/** `ammoType` values that have a recording. Lowercase, matching the seed data. */
export const SOUNDED_AMMO: Record<string, keyof typeof TRACKS> = {
  // Covers sidearms AND submachine guns, which is the point of keying on ammo
  // rather than listing eight more weapon keys.
  pistol: 'gunshotPistol',
  // The default report for anything shouldered, and now genuinely the default
  // rather than a catch-all: assault rifles and carbines land here, while
  // snipers, machine guns and shotguns all override it by key above. This
  // entry no longer stands in for anything.
  rifle: 'gunshotRifle',
  // `heavy` needs no entry: every weapon that uses it (`m249`, `pkm`, `m82`)
  // is mapped by key. A future heavy weapon with no key entry is silent, and
  // logged — which is the right default, since `heavy` spans a machine gun
  // and an anti-materiel rifle and has no single report.
};

/**
 * Minimum gap between two attack sounds, ms. A mixing guard, not a fire-rate
 * rule — it must stay well under any real weapon's cadence. 60 ms is ~16 a
 * second.
 *
 * ONE window shared by `shoot` and `swing`, not one each. A player cannot fire
 * and swing at the same instant — both come from the same LMB handler and the
 * same equipped weapon — so two independent guards would only ever serve to
 * let a gunshot and a swing overlap, which is the exact pile-up this prevents.
 */
const RETRIGGER_MS = 60;

export interface WeaponSoundHandle {
  /**
   * Fire the shot for this weapon. Unknown or unsounded weapons are no-ops.
   * `itemKey` takes precedence over `ammoType` when both have a recording.
   */
  shoot(itemKey: string | null | undefined, ammoType: string | null | undefined): void;
  /**
   * Swing the melee weapon. One recording for every melee weapon — a knife, an
   * axe and a hockey stick all move air the same way, and the animation
   * already differs per pose family.
   */
  swing(): void;
}

/** The clip a weapon should play, or `undefined` if it has none. */
export function trackForWeapon(
  itemKey: string | null | undefined,
  ammoType: string | null | undefined,
): keyof typeof TRACKS | undefined {
  if (itemKey && SOUNDED_WEAPON[itemKey]) return SOUNDED_WEAPON[itemKey];
  if (ammoType && SOUNDED_AMMO[ammoType]) return SOUNDED_AMMO[ammoType];
  return undefined;
}

/** The minimum an equipped item has to look like for the rules above. */
export interface EquippedLike {
  def: { key: string; kind: string; ammoType: string | null };
}

/**
 * The firearm a shot should come from: PRIMARY if it holds one, else
 * SECONDARY.
 *
 * Not a formality. This used to read PRIMARY alone, and every pistol-ammo
 * weapon in the catalog is a SECONDARY (`pistol`, `snub_revolver`) while only
 * rifle and shotgun classes are PRIMARY — so a player holding a pistol got no
 * muzzle flash, and a pistol sound hung off that path would have been
 * unreachable code that typechecked and passed its own tests.
 *
 * PRIMARY wins when both are firearms, so a rifle-plus-sidearm loadout fires
 * the rifle.
 */
export function equippedFirearm(
  slots: { PRIMARY?: EquippedLike | null; SECONDARY?: EquippedLike | null } | null | undefined,
): EquippedLike | undefined {
  return [slots?.PRIMARY, slots?.SECONDARY]
    .find((eq): eq is EquippedLike => eq?.def?.kind === 'FIREARM');
}

/**
 * The melee weapon a swing should come from: PRIMARY if it holds one, else
 * SECONDARY.
 *
 * Deliberately a SECOND function rather than a `kind` parameter on
 * `equippedFirearm`. That one's contract — PRIMARY-then-SECONDARY, skip
 * non-firearms, the pistol bug it was written to fix — is pinned by a block of
 * tests, and generalising it would put every one of them on a code path they
 * were not written for. Two six-line functions that each say one thing.
 *
 * The caller checks for a firearm FIRST, so a rifle-plus-knife loadout fires
 * rather than swings.
 */
export function equippedMelee(
  slots: { PRIMARY?: EquippedLike | null; SECONDARY?: EquippedLike | null } | null | undefined,
): EquippedLike | undefined {
  return [slots?.PRIMARY, slots?.SECONDARY]
    .find((eq): eq is EquippedLike => eq?.def?.kind === 'MELEE');
}

export function createWeaponSound(mixer: MixerHandle): WeaponSoundHandle {
  // Pre-warm every sounded clip: a gunshot that arrives after a network round
  // trip lands well behind its own muzzle flash.
  for (const id of new Set([
    ...Object.values(SOUNDED_WEAPON), ...Object.values(SOUNDED_AMMO), 'swingMelee' as const,
  ])) {
    void mixer.load(TRACKS[id].path).catch(() => { /* retried by the play path */ });
  }

  // Shared by shoot() and swing() — see RETRIGGER_MS.
  let lastShotAt = -Infinity;
  const warned = new Set<string>();

  return {
    shoot: (itemKey, ammoType) => {
      const id = trackForWeapon(itemKey, ammoType);
      if (!id) {
        const label = itemKey || ammoType;
        if (import.meta.env?.DEV && label && !warned.has(label)) {
          warned.add(label);
          console.info(
            `[weapon-sound] no recording for '${label}' — firing it is silent on `
            + 'purpose, not a load failure. Sounded weapons: '
            + `${Object.keys(SOUNDED_WEAPON).join(', ')}; sounded ammo: `
            + `${Object.keys(SOUNDED_AMMO).join(', ')}.`,
          );
        }
        return;
      }
      // Audio not unlocked yet. Dropping the shot beats queueing it, which
      // would fire it at whatever gesture later resumes the context.
      if (!mixer.isRunning()) return;

      const now = performance.now();
      if (now - lastShotAt < RETRIGGER_MS) return;
      lastShotAt = now;

      const track = TRACKS[id];
      void mixer.playOnce(track.path, track.channel).catch(() => {
        // A missing gunshot must not throw out of a pointer handler — the
        // click would stop doing everything else it is responsible for.
      });
    },

    swing: () => {
      // Same order of checks as shoot(), and for the same reasons: a locked
      // context must not burn the guard window, or the first audible swing
      // after audio unlocks would be swallowed too.
      if (!mixer.isRunning()) return;
      const now = performance.now();
      if (now - lastShotAt < RETRIGGER_MS) return;
      lastShotAt = now;

      const track = TRACKS.swingMelee;
      void mixer.playOnce(track.path, track.channel).catch(() => {
        // See shoot().
      });
    },
  };
}
